import { z } from 'zod';

import { DEFAULT_IR_SPLIT, InvestorRemittanceSplit } from '../loan/InvestorRemittanceSplit';
import { NonNegativeMonetaryValue, PositiveMonetaryValue } from '../validations/fields';

/*
    ----------------------
    Fee setting definitions
    ----------------------
*/
export const BaseCompanyFee = z.object({
  enabled: z.boolean().prefault(false),
  investorRemittanceSplit: InvestorRemittanceSplit.prefault({
    splitEnabled: false,
    servicer: 100,
    investor: 0,
  }),
});
export type BaseCompanyFee = z.infer<typeof BaseCompanyFee>;

// LATE
const BaseCompanyLateFee = BaseCompanyFee.extend({
  appliedToAllLoans: z.boolean(), // If false, fee will only be applied on loans with individual late fee settings
});

export const CompanyLateFeeType = z.enum(['percent', 'fixed']);
export type CompanyLateFeeType = z.infer<typeof CompanyLateFeeType>;

/**
 * How `disableIfDelinquentInterestAccruing` decides that delinquent interest is "accruing":
 * - `activelyAccruing`: DI is currently accruing at assessment time (a live, non-waived DI setting, deferred included), so DI whose accrual window has ended no longer blocks the late fee. New companies default to this via DEFAULT_COMPANY_FEE_SETTINGS.
 * - `accruedDuringArrearagePeriod`: any non-waived DI attributed to the statement's arrearage period (broad, original behavior).
 *
 * The field-level default below is `accruedDuringArrearagePeriod`, NOT the new-company default, on purpose: a pre-existing
 * company's stored blob that predates this field must parse to the original behavior rather than silently switching to the
 * new rule before the backfill runs.
 * TODO (migration followup): once 20260831backfillDelinquentInterestLateFeeLogic has run in prod, change the field-level
 * default to `activelyAccruing` so it matches the new-company default and this divergence goes away.
 */
export const DelinquentInterestLateFeeLogic = z.enum(['activelyAccruing', 'accruedDuringArrearagePeriod']);
export type DelinquentInterestLateFeeLogic = z.infer<typeof DelinquentInterestLateFeeLogic>;

export const CompanyLateFeeSettingsPercent = BaseCompanyLateFee.extend({
  type: z.literal(CompanyLateFeeType.enum.percent),
  percentCharge: z.number().nonnegative(),
  minAmount: NonNegativeMonetaryValue.default(0.01),
  maxAmount: PositiveMonetaryValue.default(10_000),
  disableIfDelinquentInterestAccruing: z.boolean().default(false),
  delinquentInterestLateFeeLogic: DelinquentInterestLateFeeLogic.default('accruedDuringArrearagePeriod'),
});
export type CompanyLateFeeSettingsPercent = z.infer<typeof CompanyLateFeeSettingsPercent>;

export const CompanyLateFeeSettingsFixed = BaseCompanyLateFee.extend({
  type: z.literal(CompanyLateFeeType.enum.fixed),
  amount: NonNegativeMonetaryValue,
  disableIfDelinquentInterestAccruing: z.boolean().default(false),
  delinquentInterestLateFeeLogic: DelinquentInterestLateFeeLogic.default('accruedDuringArrearagePeriod'),
});
export type CompanyLateFeeSettingsFixed = z.infer<typeof CompanyLateFeeSettingsFixed>;

export const CompanyLateFeeSettings = z.union([CompanyLateFeeSettingsPercent, CompanyLateFeeSettingsFixed]);
export type CompanyLateFeeSettings = z.infer<typeof CompanyLateFeeSettings>;

// DRAW
export const CompanyDrawFeeSettings = BaseCompanyFee.extend({
  amountCharge: z.number().nonnegative().prefault(150), // per draw
  deferredToPayoff: z.boolean().prefault(true),
});
export type CompanyDrawFeeSettings = z.infer<typeof CompanyDrawFeeSettings>;

// EXTENSION
export const CompanyExtensionFeeSettings = BaseCompanyFee.extend({
  percentCharge: z.number().nonnegative().prefault(0.5), // applies to original loan balance
  maxAmount: z.number().positive().prefault(10_000),
});
export type CompanyExtensionFeeSettings = z.infer<typeof CompanyExtensionFeeSettings>;

// PAYOFF STATEMENT
export const CompanyPrePenaltyFeeType = z.enum(['outstandingPrincipal', 'interest', 'fixed']);
export type CompanyPrePenaltyFeeType = z.infer<typeof CompanyPrePenaltyFeeType>;

export const CompanyPrePenaltyFeeSettings = BaseCompanyFee.extend({
  type: CompanyPrePenaltyFeeType.default('outstandingPrincipal'),
  percentageFirstYear: z.number().nonnegative().prefault(2), // used for 'OutstandingPricipal' type
  percentageSecondYear: z.number().nonnegative().prefault(2),
  percentageThirdYear: z.number().nonnegative().prefault(2),
  monthsInterest: z.int().positive().prefault(6), // used for interest type
  fixedAmount: z.number().nonnegative().prefault(3_000), // used for fixed type
  fixedMonths: z.int().positive().max(36).prefault(12),
});
export type CompanyPrePenaltyFeeSettings = z.infer<typeof CompanyPrePenaltyFeeSettings>;

export const CompanyPayoffAmountFeeSettings = BaseCompanyFee.extend({
  amountCharge: z.number().nonnegative().prefault(400),
  perProperty: z.boolean().prefault(false),
});
export type CompanyPayoffAmountFeeSettings = z.infer<typeof CompanyPayoffAmountFeeSettings>;

// TERMINATION

export const CompanyTerminationFeeType = z.enum(['percent', 'fixed']);
export type CompanyTerminationFeeType = z.infer<typeof CompanyTerminationFeeType>;

/**
 * Default values are not used here since we want fees to be required
 * to supply all values instead of filling them in with a random default.
 */
export const DEFAULT_PERCENT_TERMINATION_FEE_SETTINGS = {
  enabled: false,
  type: CompanyTerminationFeeType.enum.percent,
  percentPrincipal: 2,
  percentMonths: 9,
  investorRemittanceSplit: DEFAULT_IR_SPLIT,
};
export const DEFAULT_FIXED_TERMINATION_FEE_SETTINGS = {
  enabled: false,
  type: CompanyTerminationFeeType.enum.fixed,
  fixedAmount: 1000,
  fixedMonths: 9,
  perProperty: false,
  investorRemittanceSplit: DEFAULT_IR_SPLIT,
};

export const CompanyTerminationFeeSettingsPercent = BaseCompanyFee.extend({
  type: z.literal(CompanyTerminationFeeType.enum.percent),
  percentPrincipal: z.number().positive(),
  percentMonths: z.number().positive().int(),
});
export type CompanyTerminationFeeSettingsPercent = z.infer<typeof CompanyTerminationFeeSettingsPercent>;

export const CompanyTerminationFeeSettingsFixed = BaseCompanyFee.extend({
  type: z.literal(CompanyTerminationFeeType.enum.fixed),
  fixedAmount: PositiveMonetaryValue,
  fixedMonths: z.number().positive().int(),
  perProperty: z.boolean(),
});
export type CompanyTerminationFeeSettingsFixed = z.infer<typeof CompanyTerminationFeeSettingsFixed>;

export const CompanyTerminationFeeSettings = z.union([
  CompanyTerminationFeeSettingsPercent,
  CompanyTerminationFeeSettingsFixed,
]);
export type CompanyTerminationFeeSettings = z.infer<typeof CompanyTerminationFeeSettings>;

// PRINCIPAL LATE

export const CompanyPrincipalLateFeeType = z.enum(['percent', 'fixed']);
export type CompanyPrincipalLateFeeType = z.infer<typeof CompanyPrincipalLateFeeType>;

export const DEFAULT_PERCENT_PRINCIPAL_LATE_FEE_SETTINGS = {
  enabled: false,
  type: CompanyPrincipalLateFeeType.enum.percent,
  percentPrincipal: 2,
  percentDays: 10,
  investorRemittanceSplit: DEFAULT_IR_SPLIT,
};
export const DEFAULT_FIXED_PRINCIPAL_LATE_FEE_SETTINGS = {
  enabled: false,
  type: CompanyPrincipalLateFeeType.enum.fixed,
  fixedAmount: 1000,
  fixedDays: 10,
  perProperty: false,
  investorRemittanceSplit: DEFAULT_IR_SPLIT,
};

/**
 * Default values are not used here since we want fees to be required
 * to supply all values instead of filling them in with a random default.
 */
export const CompanyPrincipalLateFeeSettingsPercent = BaseCompanyFee.extend({
  type: z.literal(CompanyPrincipalLateFeeType.enum.percent),
  percentPrincipal: z.number().positive(),
  percentDays: z.number().positive().int(),
});
export type CompanyPrincipalLateFeeSettingsPercent = z.infer<typeof CompanyPrincipalLateFeeSettingsPercent>;

export const CompanyPrincipalLateFeeSettingsFixed = BaseCompanyFee.extend({
  type: z.literal(CompanyPrincipalLateFeeType.enum.fixed),
  fixedAmount: PositiveMonetaryValue,
  fixedDays: z.number().positive().int(),
  perProperty: z.boolean(),
});
export type CompanyPrincipalLateFeeSettingsFixed = z.infer<typeof CompanyPrincipalLateFeeSettingsFixed>;

export const CompanyPrincipalLateFeeSettings = z.union([
  CompanyPrincipalLateFeeSettingsPercent,
  CompanyPrincipalLateFeeSettingsFixed,
]);
export type CompanyPrincipalLateFeeSettings = z.infer<typeof CompanyPrincipalLateFeeSettings>;

/*
    ----------------------
    COMPANY FEES
    ----------------------
*/

export const CompanyFeeSettings = z.object({
  lateFee: CompanyLateFeeSettings,
  draw: CompanyDrawFeeSettings,
  extension: CompanyExtensionFeeSettings,
  prePenalty: CompanyPrePenaltyFeeSettings,
  lienRelease: CompanyPayoffAmountFeeSettings,
  recording: CompanyPayoffAmountFeeSettings.optional(), // TODO migration followup,
  termination: CompanyTerminationFeeSettings.prefault(DEFAULT_PERCENT_TERMINATION_FEE_SETTINGS),
  principalLate: CompanyPrincipalLateFeeSettings.prefault(DEFAULT_PERCENT_PRINCIPAL_LATE_FEE_SETTINGS),
});
export type CompanyFeeSettings = z.infer<typeof CompanyFeeSettings>;

/*
    ----------------------
    Defaults
    ----------------------
*/
export const DEFAULT_COMPANY_FEE_SETTINGS: CompanyFeeSettings = {
  lateFee: {
    enabled: false,
    appliedToAllLoans: false,
    type: 'percent',
    percentCharge: 5, // applies to billed principal + interest
    minAmount: 0.01,
    maxAmount: 10_000,
    investorRemittanceSplit: DEFAULT_IR_SPLIT,
    disableIfDelinquentInterestAccruing: false,
    // New-company default; the field-level default diverges until the backfill runs — see DelinquentInterestLateFeeLogic above.
    delinquentInterestLateFeeLogic: 'activelyAccruing',
  },
  draw: {
    enabled: false,
    amountCharge: 150, // per draw
    deferredToPayoff: true,
    investorRemittanceSplit: DEFAULT_IR_SPLIT,
  },
  extension: {
    enabled: false,
    percentCharge: 0.5, // applies to original loan balance
    maxAmount: 10_000,
    investorRemittanceSplit: DEFAULT_IR_SPLIT,
  },
  prePenalty: {
    enabled: false,
    type: 'outstandingPrincipal',
    percentageFirstYear: 2, // used for 'OutstandingPricipal' type
    percentageSecondYear: 2,
    percentageThirdYear: 2,
    monthsInterest: 6, // used for interest type
    fixedAmount: 3_000, // used for fixed type
    fixedMonths: 12,
    investorRemittanceSplit: DEFAULT_IR_SPLIT,
  },
  lienRelease: {
    enabled: false,
    amountCharge: 400,
    perProperty: false,
    investorRemittanceSplit: DEFAULT_IR_SPLIT,
  },
  recording: {
    enabled: false,
    amountCharge: 100,
    perProperty: false,
    investorRemittanceSplit: DEFAULT_IR_SPLIT,
  },
  termination: DEFAULT_PERCENT_TERMINATION_FEE_SETTINGS,
  principalLate: DEFAULT_PERCENT_PRINCIPAL_LATE_FEE_SETTINGS,
};
